import React, { useState, useEffect, useCallback } from 'react';
import { m365UsersAPI } from '../../services/api';
import { toast } from '../../utils/toast';
import Icon from '../../ui/Icon';
import { formatInTz } from '../../utils/timezone';

const s = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 16, fontWeight: 800, color: '#1A202C' },
  sub: { fontSize: 12, color: '#718096', marginTop: 4, lineHeight: 1.5 },
  banner: {
    marginBottom: 16, padding: '12px 14px', borderRadius: 10,
    background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: 12, color: '#166534', lineHeight: 1.5,
  },
  btn: (v) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
    background: v === 'primary' ? 'var(--accent)' : v === 'danger' ? '#FEF2F2' : '#F1F5F9',
    color: v === 'primary' ? '#fff' : v === 'danger' ? '#EF4444' : '#374151',
  }),
  input: {
    width: '100%', padding: '9px 11px', border: '1.5px solid #E2E8F0', borderRadius: 8,
    fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
    background: '#FAFAFA', color: '#1A202C',
  },
  label: {
    display: 'block', fontSize: 11, fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5,
  },
  th: {
    fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase',
    letterSpacing: '0.07em', padding: '10px 14px', borderBottom: '1px solid #f1f5f9',
    background: '#F8FAFF', textAlign: 'left',
  },
  td: {
    fontSize: 13, color: '#374151', padding: '11px 14px',
    borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle',
  },
  iconBtn: (v) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, padding: 0, borderRadius: 8, border: 'none', cursor: 'pointer',
    background: v === 'danger' ? '#FEF2F2' : '#EFF6FF',
    boxShadow: v === 'danger' ? 'inset 0 0 0 1px #FECACA' : 'inset 0 0 0 1px #BFDBFE',
  }),
  badge: (ok) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
    background: ok ? '#ECFDF5' : '#F3F4F6', color: ok ? '#059669' : '#6B7280',
  }),
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return formatInTz(iso, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  } catch {
    return String(iso);
  }
}

function RecordModal({ initial, onSave, onClose }) {
  const [form, setForm] = useState({
    email: '', display_name: '', is_operator: false, is_active: true, display_order: 0, ...initial,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.email?.trim()) {
      toast('Email is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        email: form.email.trim(),
        display_name: form.display_name?.trim() || '',
        is_operator: !!form.is_operator,
        is_active: form.is_active !== false,
        display_order: Number(form.display_order) || 0,
      };
      const result = initial?.id
        ? await m365UsersAPI.update(initial.id, payload)
        : await m365UsersAPI.create(payload);
      onSave(result);
    } catch (err) {
      toast(err.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, padding: '28px 28px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1A202C' }}>{initial?.id ? 'Edit M365 User' : 'Add M365 User'}</div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Icon name="close" size={18} color="#9CA3AF" /></button>
        </div>
        <p style={{ fontSize: 12, color: '#718096', marginBottom: 20 }}>
          Microsoft 365 automation allowlist only — does not change Agent Users or Requesters.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={s.label}>Email *</label>
            <input type="email" style={s.input} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="demo.user@company.com" />
          </div>
          <div>
            <label style={s.label}>Display Name</label>
            <input type="text" style={s.input} value={form.display_name || ''} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} placeholder="Demo user" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={s.label}>Operator</label>
              <select style={s.input} value={form.is_operator ? 'true' : 'false'} onChange={e => setForm(f => ({ ...f, is_operator: e.target.value === 'true' }))}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div>
              <label style={s.label}>Active</label>
              <select style={s.input} value={form.is_active ? 'true' : 'false'} onChange={e => setForm(f => ({ ...f, is_active: e.target.value === 'true' }))}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label style={s.label}>Order</label>
              <input type="number" style={s.input} value={form.display_order} onChange={e => setForm(f => ({ ...f, display_order: e.target.value }))} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button type="button" style={s.btn('default')} onClick={onClose}>Cancel</button>
          <button type="button" style={s.btn('primary')} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export default function M365UserRegistryAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await m365UsersAPI.list(search.trim() ? { search: search.trim() } : {});
      setRows(data || []);
    } catch (err) {
      toast(err.message || 'Failed to load M365 users', 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const handleDelete = async (id) => {
    try {
      await m365UsersAPI.delete(id);
      toast('M365 user deleted', 'success');
      setDeleteId(null);
      load();
    } catch (err) {
      toast(err.message || 'Delete failed', 'error');
    }
  };

  return (
    <div style={{ padding: '4px 0 8px', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div style={s.header}>
        <div>
          <div style={s.title}>M365 User</div>
          <div style={s.sub}>Allowlisted Microsoft 365 user emails for automation (reset password, onboard, etc.).</div>
        </div>
        <button type="button" style={s.btn('primary')} onClick={() => setModal({})}>
          <Icon name="plus" size={14} color="#fff" /> Add M365 User
        </button>
      </div>

      <div style={s.banner}>
        Separate from <strong>Agent Users</strong> and <strong>Requesters</strong> in this app.
      </div>

      <div style={{ marginBottom: 16, maxWidth: 360 }}>
        <input type="search" style={s.input} placeholder="Search email or name…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'auto', flex: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
          <thead>
            <tr>
              <th style={s.th}>Email</th>
              <th style={s.th}>Display Name</th>
              <th style={s.th}>Operator</th>
              <th style={s.th}>Active</th>
              <th style={s.th}>Order</th>
              <th style={s.th}>Updated</th>
              <th style={{ ...s.th, width: 88, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#718096', padding: 32 }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#718096', padding: 32 }}>
                {search ? 'No records match your search.' : 'No M365 users yet. Click Add M365 User.'}
              </td></tr>
            )}
            {!loading && rows.map(row => (
              <tr key={row.id}>
                <td style={{ ...s.td, fontWeight: 600, color: '#1A202C', fontFamily: 'monospace', fontSize: 12 }}>{row.email}</td>
                <td style={s.td}>{row.display_name || '—'}</td>
                <td style={s.td}><span style={s.badge(row.is_operator)}>{row.is_operator ? 'Yes' : 'No'}</span></td>
                <td style={s.td}><span style={s.badge(row.is_active)}>{row.is_active ? 'Active' : 'Inactive'}</span></td>
                <td style={s.td}>{row.display_order}</td>
                <td style={{ ...s.td, color: '#718096', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(row.updated_at)}</td>
                <td style={{ ...s.td, textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', gap: 6 }}>
                    <button type="button" style={s.iconBtn('edit')} onClick={() => setModal(row)} title="Edit"><Icon name="edit" size={15} color="#2563eb" /></button>
                    <button type="button" style={s.iconBtn('danger')} onClick={() => setDeleteId(row.id)} title="Delete"><Icon name="trash" size={15} color="#EF4444" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <RecordModal
          initial={modal.id ? modal : null}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); toast(modal.id ? 'Updated' : 'Created', 'success'); load(); }}
        />
      )}

      {deleteId != null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Delete M365 user?</div>
            <p style={{ fontSize: 13, color: '#718096', marginBottom: 20 }}>Automation may block this email if scope enforcement is on.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" style={s.btn('default')} onClick={() => setDeleteId(null)}>Cancel</button>
              <button type="button" style={s.btn('danger')} onClick={() => handleDelete(deleteId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
